import { Dispatch, FC, SetStateAction, useEffect, useMemo, useRef, useState } from "react"
import {
  useLazyGetServiceOrderQuery,
  usePdfCheckReqMutation,
  usePdfCreateReqMutation,
} from "../../../redux/api/content"
import { addAllMessage, selectChat, setAllMessage } from "../../../redux/slice/chat"
import { useDispatch } from "react-redux"
import Chat from "../../Chat/Chat"
import { useAppSelector } from "../../../hooks"
import clsx from "clsx"
import { getApiUrl } from "../../../utils/getApiUrl"
import { useTranslation } from "react-i18next"
import OrderCallbackModal from "./OrderCallbackModal"
import Button from "../../Assets/Button/Button"
import ModalPortal from "../../Assets/ModalPortal/ModalPortal"
import { useScrollBlock } from "../../../hooks/useScrollBlock"
import OrderDescription from "../../OrderDescription/OrderDescription"
import { useLazyGetChatMessagesQuery } from "../../../redux/api/chat"
import useWindowSize from "../../../hooks/useWindowSize"
import TitleBack from "../../TitleBack/TitleBack"
import RatingModal from "../RatingModal/RatingModal"
import { setMobileMenuIsHidden } from "../../../redux/slice/isMoreModal"
import { useLazyGetReviewsByOrderIDQuery } from "../../../redux/api/review"
import { selectUser } from "../../../redux/slice/auth"
import { IChatInfo, IChatMessage, ILastMessage } from "../../../types/chat"
import MessagesModalLoader from "../MessagesModal/MessagesModalLoader/MessagesModalLoader"
import { useLocation, useNavigate } from "react-router-dom"
import CoordinationFooter from "../../CoordinationFooter/CoordinationFooter"
import { IApprovalAssign, IServiceOrderWithReview } from "../../../types/orderTypes"
import { Channel } from "laravel-echo"
import { selectModals, setOrderModalOpen } from "../../../redux/slice/modals"
import styles from "./OrderInfoModal.module.scss"
import CoordinationStatusLabel from "../../Coordinations/CoordinationStatusLabel/CoordinationStatusLabel"
import LabelInfo from "../../Assets/LabelInfo/LabelInfo"
import { useSetViewCoordinationMutation } from "../../../redux/api/coordinations"
import { ArchiveListIcon } from "../../../icons/ArchiveListIcon"
import { getFileIco } from "../../../utils/helpers"

interface Props {
  id: string
  chatID: string
  projectID?: string
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
  delFromList?: (orderId: string) => void
  layout?: "history" | "active" | "coordination"
  modalSize?: "sm" | "lg"
  closeModalFunc?: () => void
}

const OrderInfoModal: FC<Props> = ({
  id,
  chatID,
  projectID,
  open,
  setOpen,
  delFromList = () => null,
  layout,
  modalSize,
  closeModalFunc,
}) => {
  const { t } = useTranslation("translation", { keyPrefix: `interface` })
  const { t: tStatus } = useTranslation("translation", { keyPrefix: `interface.orderStatus` })
  const dispatch = useDispatch()
  const isFirstLoadMess = useRef<any>(false)
  const { allowScroll } = useScrollBlock()
  const { isDesktop } = useWindowSize()
  const user = useAppSelector(selectUser)
  const { allMessage } = useAppSelector(selectChat)
  const { isOrderModalOpen } = useAppSelector(selectModals)
  const navigate = useNavigate()
  const location = useLocation()

  // Количество непрочитанных сообщений
  const countMessageNoRead = allMessage?.filter((ms: any) => !ms?.is_readed)?.length

  const [getServiceOrder] = useLazyGetServiceOrderQuery()
  const [pdfCheckReq, pdfCheckReqRData] = usePdfCheckReqMutation()
  const [pdfCreateReq, pdfCreateReqRData] = usePdfCreateReqMutation()

  const [
    getReviewsByOrderID,
    { data: orderReview, isFetching: isFetchingRewiew, isUninitialized: isUninitializedRewiew },
  ] = useLazyGetReviewsByOrderIDQuery()
  const [getChatMessages] = useLazyGetChatMessagesQuery()
  const [setViewCoordination] = useSetViewCoordinationMutation()
  const [openChat, setOpenChat] = useState<boolean>(false)

  const [pdfInfo, setPdfInfo] = useState<any>({ file_link: null, in_progress: false })

  const [loading, setLoading] = useState<boolean>(true)
  const [loadingChat, setLoadingChat] = useState<boolean>(true)
  const [isCanceledModal, setCanceledModal] = useState<boolean>(false)
  const [isRatingModal, setIsRatingModal] = useState<boolean>(false)
  const [dummyChats, setDummyChats] = useState<boolean>(false)
  const [isViewCoordinationFooter, setIsViewCoordinationFooter] = useState<boolean>(false)
  const isFirstRender = useRef(true)
  const [coordinationId, setCoordinationId] = useState<string>()
  const [currentLayout, setCurrentLayout] = useState<"history" | "active" | "coordination" | undefined>(layout)
  const [currentChatID, setCurrentChatID] = useState<string>(chatID)
  const [isNeedSetViewCoordination, setIsNeedSetViewCoordination] = useState<boolean>(false)
  const [orderInfo, setOrderInfo] = useState<IServiceOrderWithReview | null | undefined>(null)

  const closeModal = () => {
    dispatch(setMobileMenuIsHidden(false))
    setOpen(false)
    allowScroll(true)
    if (closeModalFunc) {
      closeModalFunc()
    }
  }

  const LabelOrder = useMemo(() => {
    const orderStatusInfo = orderInfo?.status_info
    if (!orderStatusInfo?.alias) return null
    const orderStatusInfoName = orderStatusInfo?.name
    switch (orderStatusInfo.alias) {
      case "suspended":
        return <LabelInfo txt={orderStatusInfoName} color={"danger"} className={styles.labelInfo} />
      case "cancelled":
        return <LabelInfo txt={orderStatusInfoName} color={"danger"} className={styles.labelInfo} />
      case "resolved":
        return <LabelInfo txt={orderStatusInfoName} color={"success"} className={styles.labelInfo} />
      default:
        return <LabelInfo txt={orderStatusInfoName} className={styles.labelInfo} />
    }
  }, [layout, orderInfo?.status_info])

  useEffect(() => {
    if (!open || !id) return
    if (!isDesktop && isOrderModalOpen) return
    if (location.hash.includes("openChat")) return
    navigate({
      hash: "orderModal",
      search: `?order_id=${id}&project_id=${projectID}`,
    })

    return () => {
      navigate({
        hash: "",
        search: "",
      })
      dispatch(setOrderModalOpen(false))
    }
  }, [open, id, projectID, isDesktop, isOrderModalOpen])

  useEffect(() => {
    if (typeof window.Echo === "undefined" || !currentChatID) return
    const channel = window.Echo.join(`chats.${currentChatID}`)
    channel.listen(".client-addMessage", (event: any) => {
      dispatch(addAllMessage(event))
    })

    const channelPrivate = window.Echo.private(`privateUser.${user?.id}`)

    channelPrivate?.listen(".newMessageInDialog", (event: { dialog: IChatInfo; message: ILastMessage }) => {
      const isExistMessage = allMessage.some((mes: IChatMessage) => mes.id === event?.message?.id)
      if (isExistMessage) return
      dispatch(addAllMessage(event.message))
    })

    channelPrivate?.listen(".changeMessageInDialog", (event: ILastMessage) => {
      dispatch(
        setAllMessage(
          allMessage.map((item: any) => {
            if (item.id === event.id) item = { ...item, ...event }
            return item
          }),
        ),
      )
    })
    channelPrivate?.listen(".deleteMessageInDialog", (event: ILastMessage) => {
      dispatch(setAllMessage(allMessage.filter((item: any) => item.id !== event.id)))
    })
    channelPrivate?.listen(".pdfCreatedOrder", (event: any) => {
      setPdfInfo({ file_link: event?.file_link, in_progress: false })
    })

    return () => {
      if (allMessage?.length && !open) {
        window.Echo.leave(`chats.${currentChatID}`)
        channelPrivate.stopListening(`.newMessageInDialog`)
        channelPrivate.stopListening(`.changeMessageInDialog`)
        channelPrivate.stopListening(`.deleteMessageInDialog`)
        channelPrivate.stopListening(`.pdfCreatedOrder`)
      }
    }
  }, [open, currentChatID, user?.id, allMessage])

  useEffect(() => {
    if (!id) return
    const channel = window?.Echo?.private(`publicOrder.${id}`)

    channel?.listen(".publicOrderUpdateApproves", (event: { id: string }) => {
      getOrderFunc(event.id)
    })

    return () => {
      channel.stopListening(`.publicOrderUpdateApproves`)
      window.Echo?.leave(`publicOrder.${id}`)
    }
  }, [id])

  useEffect(() => {
    if (!user?.id || layout === "coordination" || typeof window.Echo === "undefined") return
    const channel: Channel = window?.Echo?.private(`privateUser.${user?.id}`)
    channel?.listen(".changeStatusPublicOrder", (event: IServiceOrderWithReview) => {
      if (id === event.id) {
        setCurrentLayout(event.status === 0 ? "active" : "history")
        setOrderInfo((prev) => {
          if (!prev) return
          return {
            ...prev,
            status_alias: event.status_alias,
            status: event.status,
            status_info: event?.status_info || prev?.status_info,
          }
        })
      }
    })
  }, [user?.id, layout, id])

  // Запуск генерации отчёта
  const generateReport = () => {
    setPdfInfo({ file_link: null, in_progress: true })
    pdfCreateReq({ id: id, project_id: projectID || "" }).catch(() =>
      setPdfInfo({ file_link: null, in_progress: false }),
    )
  }

  const getOrderFunc = async (id: string) => {
    if (!id) return
    // Проверка на готов ли пдф
    pdfCheckReq({ id: id, project_id: projectID || "" }).then((result: any) => {
      const res = result?.data?.data
      setPdfInfo(res)
    })
    await getServiceOrder({ id: id, project_id: projectID || "" })
      .then(async (resp) => {
        if (resp && Array.isArray(resp?.data) && !resp?.data?.length) {
          closeModal()
          console.warn(`getServiceOrder by ${id} is empty data`)
        } else {
          setLoading(false)
          setOrderInfo(resp?.data)
          if (resp?.data?.chat_dialog_id) {
            setCurrentChatID(resp?.data?.chat_dialog_id)
            if (layout !== "coordination") {
              setCurrentLayout(resp.data.status === 0 ? "active" : "history")
            }
            await getChatMessages({ id: resp.data.chat_dialog_id, project_id: projectID || "" })
              .then((res: any) => {
                isFirstLoadMess.current = true
                dispatch(setAllMessage(res.data.aMessages))
                setLoadingChat(false)
              })
              .catch((er) => console.error(er.message))
          } else {
            setDummyChats(true)
          }
        }
      })
      .catch((err) => console.error(err))
    isFirstRender.current = false
    getReviewsByOrderID(id)
  }

  useEffect(() => {
    if (!id) return
    getOrderFunc(id)
  }, [id])

  useEffect(() => {
    if (!isDesktop) {
      setOpenChat(location.hash.includes("#openChat"))
    }
  }, [location.hash])

  useEffect(() => {
    const currentApprovals = orderInfo?.approvals?.find((approval) => approval.status === "in_progress")

    const currentUserApprove = currentApprovals?.assignees?.find((approval: IApprovalAssign) => {
      return approval?.concordant?.id === user?.id && approval.status === "in_progress"
    })

    const idCurrentUserApprove = currentUserApprove?.id

    setCoordinationId(idCurrentUserApprove)
    setIsViewCoordinationFooter(!!idCurrentUserApprove)
    setIsNeedSetViewCoordination(!currentUserApprove?.viewed_at)
  }, [orderInfo, user])

  useEffect(() => {
    if (isNeedSetViewCoordination && coordinationId) {
      setViewCoordination({ id: coordinationId, project_id: projectID || "" })
      setIsNeedSetViewCoordination(false)
    }
  }, [isNeedSetViewCoordination, coordinationId, projectID])

  const isViewCoordinationLabel = useMemo(() => {
    return layout === "active" && !!orderInfo?.approval_status && orderInfo?.status === 0
  }, [layout, orderInfo])

  const [iconVisible, iconSrc] = getFileIco("pdf")

  const btnOrder = useMemo(() => {
    if (!orderInfo?.id) return null
    if (orderInfo?.ordered_by !== user?.id) return null
    if (!currentLayout || !["active", "history"].includes(currentLayout)) return null
    if (currentLayout === "active") {
      return <Button txt={t("cancel")} mode={"warning"} size={"sm"} onClick={() => setCanceledModal(true)} />
    }
    return <Button txt={t("returnToWork")} mode={"warning"} size={"sm"} onClick={() => setCanceledModal(true)} />
  }, [currentLayout, orderInfo, user])

  return (
    <>
      <ModalPortal
        isOpen={open}
        setIsOpen={() => {
          setOpen(!open)
          if (closeModalFunc) {
            closeModalFunc()
          }
        }}
        className={clsx(currentLayout && !isDesktop && styles["active-or-history-modal"], {
          [styles["opened-mobile-chat"]]: openChat && !isDesktop,
          [styles["no-margin-closeBtn"]]: isViewCoordinationLabel || orderInfo?.status === -1,
        })}
        header={
          <>
            {isDesktop ? (
              <>
                <h2 className={clsx("modal__title", styles.title, loading && "skeletonBlock")}>
                  {orderInfo?.name} {orderInfo?.count && orderInfo.count > 1 ? <span>x{orderInfo.count}</span> : ""}
                </h2>

                {LabelOrder}

                {isViewCoordinationLabel && !!orderInfo?.approval_status && (
                  <CoordinationStatusLabel
                    className={styles["coordination-status-label"]}
                    status={orderInfo?.approval_status}
                  />
                )}

                {!loading && layout === "history" && (
                  <div className={styles.pdfBlock}>
                    {pdfInfo?.file_link ? (
                      <a
                        href={`${pdfInfo?.file_link}`}
                        rel="noreferrer"
                        className={clsx(styles.pdfBlock__download)}
                        download
                      >
                        <img src={`${iconSrc ?? ""}`} alt="" /> {t("saveReportPdf")}
                      </a>
                    ) : (
                      <button
                        type="button"
                        className={clsx(styles.pdfBlock__btn, pdfInfo?.in_progress ? styles.loading : "")}
                        onClick={generateReport}
                      >
                        {pdfInfo?.in_progress ? t("reportGenerating") : t("generateReport")}
                        {pdfInfo?.in_progress ? (
                          <div className={clsx(styles.pdfBlock__loaderWp)}>
                            <img
                              className={clsx(styles.btn__loader, "btn__loader")}
                              src={"/img/loader_blue.png"}
                              alt="Loading..."
                            />
                          </div>
                        ) : (
                          ArchiveListIcon()
                        )}
                      </button>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                {loading ? (
                  <h2 className={clsx(styles.title, loading && "skeletonBlock")} />
                ) : (
                  <div className={styles.mobHeaderFlex}>
                    <TitleBack
                      title={`${orderInfo?.name}
                        ${orderInfo?.count && orderInfo.count > 1 ? `x${orderInfo.count}` : ""}`}
                      onClick={() => {
                        if (openChat) {
                          setOpenChat(false)
                          navigate({
                            hash: location.hash?.replace("#openChat", ""),
                            search: `?order_id=${id}&project_id=${projectID}`,
                          })
                        } else {
                          navigate({
                            hash: "",
                            search: "",
                          })
                          closeModal()
                        }
                      }}
                      noLinkBack
                    />
                    {!loading && layout === "history" && (
                      <div className={styles.pdfBlock}>
                        {pdfInfo?.file_link ? (
                          <a
                            href={`${pdfInfo?.file_link}`}
                            rel="noreferrer"
                            className={clsx(styles.pdfBlock__download)}
                            download
                          >
                            <img src={`${iconSrc ?? ""}`} alt="" /> {t("saveReportPdf")}
                          </a>
                        ) : (
                          <button
                            type="button"
                            className={clsx(styles.pdfBlock__btn, pdfInfo?.in_progress ? styles.loading : "")}
                            onClick={generateReport}
                          >
                            {pdfInfo?.in_progress ? t("reportGenerating") : t("generateReport")}
                            {pdfInfo?.in_progress ? (
                              <div className={clsx(styles.pdfBlock__loaderWp)}>
                                <img
                                  className={clsx(styles.btn__loader, "btn__loader")}
                                  src={"/img/loader_blue.png"}
                                  alt="Loading..."
                                />
                              </div>
                            ) : (
                              ArchiveListIcon()
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        }
        isCloseBtn={isDesktop}
        bodyNoPadding
        size={modalSize}
      >
        <div className={styles.main}>
          {!isDesktop && openChat ? null : (
            <aside className={clsx(styles.aside, currentLayout === "coordination" && styles["aside-lg"])}>
              {orderInfo && !openChat && (
                <OrderDescription isLoading={loading} order={orderInfo} review={orderReview} layout={currentLayout} />
              )}

              {isDesktop ? (
                <>
                  <footer
                    className={clsx(
                      styles.footer,
                      isViewCoordinationFooter && styles["footer__painted"],
                      currentLayout && ["active", "history"].includes(currentLayout) && styles["column"],
                    )}
                  >
                    {isViewCoordinationFooter && (
                      <CoordinationFooter
                        coordinationId={coordinationId}
                        projectID={projectID || ""}
                        setIsViewCoordinationFooter={setIsViewCoordinationFooter}
                        getOrderFunc={() => getOrderFunc(id)}
                      />
                    )}

                    {btnOrder}
                  </footer>
                </>
              ) : (
                <div className={`${styles["btn-container"]}`}>
                  {isViewCoordinationFooter && (
                    <CoordinationFooter
                      coordinationId={coordinationId}
                      projectID={projectID || ""}
                      setIsViewCoordinationFooter={setIsViewCoordinationFooter}
                      getOrderFunc={() => getOrderFunc(id)}
                    />
                  )}
                  {(currentLayout === "active" || currentLayout === "history") && (
                    <div className={`${styles["btn-container__topTwoBtns"]}`}>
                      {btnOrder}

                      {!isUninitializedRewiew &&
                        !isFetchingRewiew &&
                        currentLayout === "history" &&
                        orderInfo?.status === 1 &&
                        (!orderReview || !orderReview.length) && (
                          <Button
                            txt={t("evaluate")}
                            size={"sm"}
                            onClick={() => {
                              setIsRatingModal(true)
                            }}
                          />
                        )}
                    </div>
                  )}

                  <div className={`${styles["btn-container__bottomTwoBtns"]}`}>
                    <Button
                      txt={t("back")}
                      type="button"
                      onClick={() => {
                        closeModal()
                      }}
                      className={`btn btn_grey ${styles["back-btn"]}`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setOpenChat(true)
                        navigate({
                          hash: location.hash + "#openChat",
                          search: `?order_id=${id}&project_id=${projectID}`,
                        })
                      }}
                      className={`btn btn_grey ${styles["messenger-btn"]}`}
                    >
                      <img src="/img/chat.svg" alt="" />
                      {t("messages")}
                      {!!countMessageNoRead && (
                        <span className={`${styles.messagesNoReview}`}>{countMessageNoRead}</span>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </aside>
          )}
          {(isDesktop || openChat) && (
            <>
              {dummyChats ? (
                <div className={styles.dummyChat} />
              ) : loadingChat ? (
                <MessagesModalLoader />
              ) : (
                <Chat
                  chatID={currentChatID}
                  orderInfo={orderInfo}
                  projectID={projectID}
                  isFirstLoadMess={isFirstLoadMess.current}
                  className={!isDesktop ? styles["mobile-chat"] : styles["order-chat"]}
                  emptyTxt={t("emptyTxtChatOrder")}
                  isViewChat={openChat}
                />
              )}
            </>
          )}
        </div>
      </ModalPortal>

      {isCanceledModal && orderInfo?.id && (
        <OrderCallbackModal
          layout={currentLayout}
          open={isCanceledModal}
          setOpen={setCanceledModal}
          orderId={orderInfo.id}
          projectID={projectID || ""}
          delFromList={delFromList}
          callbackClose={() => closeModal()}
          className={!isDesktop ? "order-info-callback-modal" : ""}
        />
      )}
      {currentLayout === "history" && orderInfo?.id && (
        <RatingModal
          orderId={orderInfo?.id}
          open={isRatingModal}
          projectID={projectID || ""}
          setOpen={setIsRatingModal}
        />
      )}
    </>
  )
}

export default OrderInfoModal
